// src/services/agreementService.ts

// Use the environment variable for API URL, with fallback to '/api'
// When using the proxy configuration in vite.config.ts, we should use '/api' as the base URL
const API_URL = import.meta.env.VITE_API_URL || '/api';

// Log the API URL to help with debugging
console.log('API_URL:', API_URL);

// Helper function to extract file metadata from base64 string
const extractFileMetadata = (base64String: string, individualId: string) => {
  const arr = base64String.split(',');
  const mimeType = arr[0].match(/:(.*?);/)?.[1] || 'application/octet-stream';
  const base64Data = arr[1] || base64String;
  
  // Calculate file size from base64 data (4 base64 chars represent 3 bytes)
  const fileSize = Math.floor((base64Data.length * 3) / 4);
  
  // Determine file extension and name based on MIME type
  const fileExtension = mimeType.includes('jpeg') ? 'jpg' :
                       mimeType.includes('png') ? 'png' :
                       mimeType.includes('gif') ? 'gif' :
                       mimeType.includes('pdf') ? 'pdf' : 'jpg';
  
  const fileName = `id_document_${individualId}.${fileExtension}`;
  
  return {
    fileName,
    fileType: mimeType,
    fileSize,
    fileExtension
  };
};

// Start a new agreement
export const startAgreement = async () => {
  console.log(`Starting agreement at: ${API_URL}/agreements_v2/start`);
  const response = await fetch(`${API_URL}/agreements_v2/start`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({}),
  });
  
  if (!response.ok) {
    const errorText = await response.text();
    console.error(`Error starting agreement: ${errorText}`);
    throw new Error('Failed to start agreement');
  }
  
  return response.json();
};

// Get agreement by ID
export const getAgreementById = async (agreementId: string) => {
  console.log(`Fetching agreement with ID: ${agreementId}`);
  console.log(`API URL: ${API_URL}/agreements_v2/${agreementId}`);
  
  try {
    const response = await fetch(`${API_URL}/agreements_v2/${agreementId}`);
    console.log(`Response status: ${response.status}`);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Error response: ${errorText}`);
      
      // Handle specific error cases
      if (response.status === 404 || response.status === 422) {
        const error = new Error('Agreement not found');
        (error as any).status = response.status;
        (error as any).agreementId = agreementId;
        throw error;
      } else if (response.status === 403) {
        const error = new Error('Access denied to this agreement');
        (error as any).status = 403;
        throw error;
      } else {
        const error = new Error(`Failed to fetch agreement: ${errorText}`);
        (error as any).status = response.status;
        throw error;
      }
    }
    
    const data = await response.json();
    console.log("Agreement data received:", data);
    return data;
  } catch (error) {
    console.error("Error fetching agreement:", error);
    throw error;
  }
};

// Submit purchaser details
export const submitPurchaserDetails = async (agreementId: string, details: any) => {
  console.log(`Submitting purchaser details for agreement: ${agreementId}`);
  const response = await fetch(`${API_URL}/agreements_v2/${agreementId}/submit-details`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      purchaser_details: details
    }),
  });
  
  if (!response.ok) {
    const errorText = await response.text();
    console.error(`Error submitting purchaser details: ${errorText}`);
    throw new Error('Failed to submit purchaser details');
  }
  
  return response.json();
};

// Submit signature
export const submitSignature = async (agreementId: string, signatureData: string) => {
  console.log(`Submitting signature for agreement: ${agreementId}`);
  const response = await fetch(`${API_URL}/agreements_v2/${agreementId}/sign`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      signature_data: signatureData
    }),
  });
  
  if (!response.ok) {
    const errorText = await response.text();
    console.error(`Error submitting signature: ${errorText}`);
    throw new Error('Failed to submit signature');
  }
  
  return response.json();
};

// Submit signature by signature ID (for secondary signers)
export const submitSignatureById = async (signatureId: string, signatureData: string) => {
  console.log(`Submitting signature by ID: ${signatureId}`);
  const response = await fetch(`${API_URL}/agreements_v2/signature/${signatureId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      signature_data: signatureData
    }),
  });
  
  if (!response.ok) {
    const errorText = await response.text();
    console.error(`Error submitting signature by ID: ${errorText}`);
    throw new Error('Failed to submit signature');
  }
  
  return response.json();
};

// Get agreement by signature ID (for secondary signers)
export const getAgreementBySignatureId = async (signatureId: string) => {
  console.log(`Getting agreement by signature ID: ${signatureId}`);
  
  try {
    // The backend route is /agreements_v2/signature/{signature_id}/check-status
    // Let's try to get the agreement ID first using this route
    const checkStatusUrl = `${API_URL}/agreements_v2/signature/${signatureId}/check-status`;
    console.log(`Checking signature status at: ${checkStatusUrl}`);
    
    const statusResponse = await fetch(checkStatusUrl);
    if (!statusResponse.ok) {
      const errorText = await statusResponse.text();
      console.error(`Error checking signature status: ${errorText}`);
      throw new Error(`Failed to check signature status: ${errorText}`);
    }
    
    const statusData = await statusResponse.json();
    console.log(`Signature status data:`, statusData);
    
    // If the agreement_id is not in the status response, we need to query the database to get it
    if (!statusData.agreement_id) {
      console.log('No agreement_id in status response, querying database');
      
      // Use the /agreements_v2/sign/{signature_id} endpoint to get the agreement
      const signEndpoint = `${API_URL}/agreements_v2/sign/${signatureId}`;
      console.log(`Fetching agreement from sign endpoint: ${signEndpoint}`);
      
      const signResponse = await fetch(signEndpoint);
      if (!signResponse.ok) {
        const errorText = await signResponse.text();
        console.error(`Error fetching agreement from sign endpoint: ${errorText}`);
        throw new Error(`Failed to fetch agreement: ${errorText}`);
      }
      
      const agreementData = await signResponse.json();
      console.log(`Successfully fetched agreement data for signature ID ${signatureId}`);
      
      // Add the signature ID and status data to the response for reference
      agreementData.signature_id = signatureId;
      agreementData.signature_status = statusData;
      
      return agreementData;
    }
    
    // If we have the agreement ID, fetch the full agreement
    const agreementId = statusData.agreement_id;
    console.log(`Found agreement ID: ${agreementId}, fetching full agreement`);
    
    const agreementResponse = await fetch(`${API_URL}/agreements_v2/${agreementId}`);
    if (!agreementResponse.ok) {
      const errorText = await agreementResponse.text();
      console.error(`Error fetching agreement: ${errorText}`);
      throw new Error(`Failed to fetch agreement: ${errorText}`);
    }
    
    const agreementData = await agreementResponse.json();
    console.log(`Successfully fetched agreement data for signature ID ${signatureId}`);
    
    // Add the signature ID to the response for reference
    agreementData.signature_id = signatureId;
    
    return agreementData;
  } catch (error) {
    console.error(`Error in getAgreementBySignatureId: ${error instanceof Error ? error.message : String(error)}`);
    throw error;
  }
};

// Process payment
export const processPayment = async (agreementId: string) => {
  console.log(`Processing payment for agreement: ${agreementId}`);
  const response = await fetch(`${API_URL}/agreements_v2/${agreementId}/pay`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  
  if (!response.ok) {
    const errorText = await response.text();
    console.error(`Error processing payment: ${errorText}`);
    throw new Error('Failed to process payment');
  }
  
  return response.json();
};

// Download agreement PDF (with watermark for preview/draft downloads)
export const downloadAgreementPdf = async (agreementId: string) => {
  console.log(`Downloading PDF for agreement: ${agreementId} (with watermark)`);
  window.location.href = `${API_URL}/agreements_v2/${agreementId}/download?show_watermark=true`;
};

// Test API connection
export const testApiConnection = async () => {
  console.log(`Testing API connection to: ${API_URL}/agreements_v2/test`);
  
  try {
    const response = await fetch(`${API_URL}/agreements_v2/test`);
    console.log(`Test response status: ${response.status}`);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Test error response: ${errorText}`);
      return {
        success: false,
        status: response.status,
        message: errorText
      };
    }
    
    const data = await response.json();
    console.log("Test response data:", data);
    return {
      success: true,
      status: response.status,
      data
    };
  } catch (error) {
    console.error("Error testing API connection:", error);
    return {
      success: false,
      message: error instanceof Error ? error.message : String(error)
    };
  }
};

// Update agreement status
export const updateAgreementStatus = async (agreementId: string, status: string) => {
  console.log(`Updating agreement status for ID: ${agreementId} to ${status}`);
  const response = await fetch(`${API_URL}/agreements_v2/${agreementId}/status`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ status }),
  });
  
  if (!response.ok) {
    const errorText = await response.text();
    console.error(`Error updating agreement status: ${errorText}`);
    throw new Error('Failed to update agreement status');
  }
  
  return response.json();
};

// Upload ID file immediately
export const uploadIdFile = async (agreementId: string, individualId: string, fileData: string) => {
  console.log(`Service - Uploading ID file for agreement: ${agreementId}, individual: ${individualId}`);
  
  try {
    // Calculate approximate size of base64 data for logging
    let base64Size = 0;
    let base64Data = '';
    if (fileData.includes(',')) {
      base64Data = fileData.split(',')[1];
      base64Size = base64Data.length;
    } else {
      base64Data = fileData;
      base64Size = fileData.length;
    }
    
    // Estimate original file size (4 base64 chars represent 3 bytes)
    const estimatedSizeBytes = Math.floor((base64Size * 3) / 4);
    const estimatedSizeMB = estimatedSizeBytes / (1024 * 1024);
    const maxSizeMB = 10;
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    const bufferSizeMB = 9.5; // Buffer to prevent boundary issues
    const bufferSizeBytes = bufferSizeMB * 1024 * 1024;
    
    // Calculate base64 overhead
    const base64Overhead = base64Size - (estimatedSizeBytes * 4 / 3);
    const base64OverheadPercent = (base64Overhead / base64Size) * 100;
    
    console.log(`Service - Base64 data length: ${base64Size} characters`);
    console.log(`Service - Estimated file size: ${estimatedSizeMB.toFixed(2)}MB (${estimatedSizeBytes} bytes)`);
    console.log(`Service - Max allowed size: ${maxSizeMB}MB (${maxSizeBytes} bytes)`);
    console.log(`Service - Buffer size limit: ${bufferSizeMB}MB (${bufferSizeBytes} bytes)`);
    console.log(`Service - Base64 overhead: ${(base64Overhead / 1024).toFixed(2)}KB (${base64OverheadPercent.toFixed(2)}%)`);
    
    // Pre-check file size before sending to server using buffer limit
    if (estimatedSizeBytes > bufferSizeBytes) {
      console.log(`Service - Estimated file size exceeds buffer limit: ${estimatedSizeMB.toFixed(2)}MB > ${bufferSizeMB}MB`);
      throw new Error(`File size (${estimatedSizeMB.toFixed(2)}MB) exceeds the recommended limit of ${bufferSizeMB}MB`);
    } else {
      console.log(`Service - Estimated file size within buffer limit: ${estimatedSizeMB.toFixed(2)}MB <= ${bufferSizeMB}MB`);
    }
    
    // Extract file metadata from base64 string
    const fileMetadata = extractFileMetadata(fileData, individualId);
    
    console.log(`Service - File metadata: ${fileMetadata.fileName} (${fileMetadata.fileType}, ${fileMetadata.fileSize} bytes)`);
    
    // Create JSON payload for sending data
    const requestBody = {
      individual_id: individualId,
      file_data: fileData,
      file_name: fileMetadata.fileName,
      file_type: fileMetadata.fileType,
      file_size: fileMetadata.fileSize
    };
    
    // Send data as JSON
    console.log(`Service - Sending request to: ${API_URL}/agreements_v2/${agreementId}/upload-id-file`);
    const response = await fetch(`${API_URL}/agreements_v2/${agreementId}/upload-id-file`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody)
    });
    
    console.log(`Service - Response status: ${response.status}`);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Service - Error uploading ID file: ${errorText}`);
      
      // Provide more specific error messages based on status code
      if (response.status === 413) {
        console.error(`Service - 413 Payload Too Large error with file size: ${estimatedSizeMB.toFixed(2)}MB`);
        throw new Error(`File size (${estimatedSizeMB.toFixed(2)}MB) exceeds the maximum allowed limit of 10MB`);
      } else if (response.status === 400) {
        // Check if the error message contains any indication of file size issues
        if (errorText.toLowerCase().includes('size') ||
            errorText.toLowerCase().includes('large') ||
            errorText.toLowerCase().includes('limit') ||
            errorText.toLowerCase().includes('exceed')) {
          console.error(`Service - 400 Bad Request with size-related error: ${errorText}`);
          
          // Try to extract the actual file size from the error message if available
          let actualSize = estimatedSizeMB.toFixed(2);
          const sizeMatch = errorText.match(/(\d+(\.\d+)?)\s*MB/i);
          if (sizeMatch && sizeMatch[1]) {
            actualSize = sizeMatch[1];
            console.error(`Service - Extracted actual file size from error: ${actualSize}MB`);
          }
          
          throw new Error(`File size (${actualSize}MB) exceeds the maximum allowed limit of 10MB`);
        } else {
          console.error(`Service - 400 Bad Request with error: ${errorText}`);
          throw new Error(`Bad Request: ${errorText}`);
        }
      } else if (response.status === 404) {
        console.error(`Service - 404 Not Found error: ${errorText}`);
        if (errorText.toLowerCase().includes('individual not found')) {
          throw new Error(`Individual record not found. Please ensure all details are saved before uploading.`);
        } else {
          throw new Error(`Resource not found: ${errorText}`);
        }
      } else {
        console.error(`Service - Error with status ${response.status}: ${errorText}`);
        throw new Error(`Failed to upload ID file (${response.status}): ${errorText}`);
      }
    }
    
    const result = await response.json();
    console.log(`Service - Upload successful, result:`, result);
    return result;
  } catch (error) {
    console.error("Service - Error uploading ID file:", error);
    throw error;
  }
};

// Delete ID file
export const deleteIdFile = async (agreementId: string, individualId: string) => {
  console.log(`Deleting ID file for agreement: ${agreementId}, individual: ${individualId}`);
  
  try {
    const response = await fetch(`${API_URL}/agreements_v2/${agreementId}/delete-id-file?individual_id=${individualId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Error deleting ID file: ${errorText}`);
      throw new Error('Failed to delete ID file');
    }
    
    return response.json();
  } catch (error) {
    console.error("Error deleting ID file:", error);
    throw error;
  }
};

// Get ID files for an agreement
export const getIdFiles = async (agreementId: string) => {
  console.log(`Fetching ID files for agreement: ${agreementId}`);
  
  try {
    const response = await fetch(`${API_URL}/agreements_v2/${agreementId}/id-files`, {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Error fetching ID files: ${errorText}`);
      throw new Error('Failed to fetch ID files');
    }
    
    const data = await response.json();
    console.log("ID files response data:", data);
    
    // Transform the data into a more usable format for the frontend
    // The backend returns an array of objects, but we want a map of personId -> id_file
    const filesMap: Record<string, string> = {};
    const filesDataMap: Record<string, { id_url: string, id_type: string }> = {};
    
    if (Array.isArray(data)) {
      data.forEach(item => {
        if (item.id && item.id_file) {
          // Handle both old format (string) and new format (object with id_url and id_type)
          if (typeof item.id_file === 'string') {
            filesMap[item.id] = item.id_file;
            filesDataMap[item.id] = { id_url: item.id_file, id_type: 'unknown' };
          } else if (typeof item.id_file === 'object' && item.id_file.id_url) {
            filesMap[item.id] = item.id_file.id_url; // For backward compatibility
            filesDataMap[item.id] = item.id_file;
          }
        }
      });
      
      return {
        files: filesMap,
        filesData: filesDataMap
      };
    }
    
    return data;
  } catch (error) {
    console.error("Error fetching ID files:", error);
    throw error;
  }
};

// Get presigned URL for an ID file
export const getPresignedUrl = async (agreementId: string, individualId: string) => {
  console.log(`Fetching presigned URL for agreement: ${agreementId}, individual: ${individualId}`);
  
  try {
    const response = await fetch(`${API_URL}/agreements_v2/${agreementId}/presigned-url?individual_id=${individualId}`, {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Error fetching presigned URL: ${errorText}`);
      throw new Error('Failed to fetch presigned URL');
    }
    
    const data = await response.json();
    console.log("Presigned URL response:", data);
    return data.presigned_url;
  } catch (error) {
    console.error("Error fetching presigned URL:", error);
    throw error;
  }
};

// Save signature for an individual
export const saveSignature = async (agreementId: string, individualId: string, signatureData: string) => {
  console.log(`Service - Saving signature for agreement: ${agreementId}, individual: ${individualId}`);
  
  try {
    // Create FormData object for sending data
    const formData = new FormData();
    formData.append('individual_id', individualId);
    formData.append('signature_data', signatureData);
    
    // Send data as form data
    console.log(`Service - Sending signature save request to: ${API_URL}/agreements_v2/${agreementId}/save-signature`);
    const response = await fetch(`${API_URL}/agreements_v2/${agreementId}/save-signature`, {
      method: 'POST',
      body: formData
    });
    
    console.log(`Service - Signature save response status: ${response.status}`);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Service - Error saving signature: ${errorText}`);
      throw new Error(`Failed to save signature: ${errorText}`);
    }
    
    const result = await response.json();
    console.log(`Service - Signature save successful, result:`, result);
    
    // The result should contain signature_id and signature_url
    if (!result.signature_id || !result.signature_url) {
      console.warn("Service - Missing signature_id or signature_url in response:", result);
    }
    
    return result;
  } catch (error) {
    console.error("Service - Error saving signature:", error);
    throw error;
  }
};

// Check signature status for the primary signer
export const checkPrimarySignatureStatus = async (agreementId: string) => {
  console.log(`Checking primary signature status for agreement: ${agreementId}`);
  
  try {
    const response = await fetch(`${API_URL}/agreements_v2/${agreementId}/check-signature-status`);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Error checking primary signature status: ${errorText}`);
      throw new Error('Failed to check signature status');
    }
    
    const data = await response.json();
    console.log("Primary signature status data:", data);
    return data;
  } catch (error) {
    console.error("Error checking primary signature status:", error);
    throw error;
  }
};

// Check signature status for a secondary signer
export const checkSecondarySignatureStatus = async (signatureId: string) => {
  console.log(`Checking secondary signature status for signature: ${signatureId}`);
  
  try {
    const response = await fetch(`${API_URL}/agreements_v2/signature/${signatureId}/check-status`);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Error checking secondary signature status: ${errorText}`);
      throw new Error('Failed to check signature status');
    }
    
    const data = await response.json();
    console.log("Secondary signature status data:", data);
    return data;
  } catch (error) {
    console.error("Error checking secondary signature status:", error);
    throw error;
  }
};

// Get Xero payment URL for an agreement
export const getXeroPaymentUrl = async (agreementId: string) => {
  console.log(`Getting Xero payment URL for agreement: ${agreementId}`);
  
  try {
    const response = await fetch(`${API_URL}/agreements_v2/${agreementId}/xero-payment-url`);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Error getting Xero payment URL: ${errorText}`);
      throw new Error('Failed to get Xero payment URL');
    }
    
    const data = await response.json();
    console.log("Xero payment URL response:", data);
    return data;
  } catch (error) {
    console.error("Error getting Xero payment URL:", error);
    throw error;
  }
};

// Download Xero invoice PDF
export const downloadXeroInvoicePdf = async (agreementId: string) => {
  console.log(`Downloading Xero invoice PDF for agreement: ${agreementId}`);
  
  try {
    const response = await fetch(`${API_URL}/agreements_v2/${agreementId}/xero-invoice-pdf`);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Error downloading Xero invoice PDF: ${errorText}`);
      throw new Error('Failed to download Xero invoice PDF');
    }
    
    // Create a blob from the response
    const blob = await response.blob();
    
    // Create a temporary URL for the blob
    const url = window.URL.createObjectURL(blob);
    
    // Create a temporary anchor element and trigger download
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoice-${agreementId}.pdf`;
    document.body.appendChild(a);
    a.click();
    
    // Clean up
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    
    console.log(`Successfully downloaded Xero invoice PDF for agreement: ${agreementId}`);
  } catch (error) {
    console.error("Error downloading Xero invoice PDF:", error);
    throw error;
  }
};

// Download signed agreement PDF (without watermark for final downloads)
export const downloadSignedAgreementPdf = async (agreementId: string) => {
  console.log(`Downloading signed agreement PDF for agreement: ${agreementId} (without watermark)`);
  
  try {
    const response = await fetch(`${API_URL}/agreements_v2/${agreementId}/signed-agreement-pdf?show_watermark=false`);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Error downloading signed agreement PDF: ${errorText}`);
      throw new Error('Failed to download signed agreement PDF');
    }
    
    // Create a blob from the response
    const blob = await response.blob();
    
    // Create a temporary URL for the blob
    const url = window.URL.createObjectURL(blob);
    
    // Create a temporary anchor element and trigger download
    const a = document.createElement('a');
    a.href = url;
    a.download = `signed-agreement-${agreementId}.pdf`;
    document.body.appendChild(a);
    a.click();
    
    // Clean up
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    
    console.log(`Successfully downloaded signed agreement PDF for agreement: ${agreementId}`);
  } catch (error) {
    console.error("Error downloading signed agreement PDF:", error);
    throw error;
  }
};

// Backward compatibility function
export const checkSignatureStatus = async (agreementId: string, userId?: string) => {
  console.log(`Using legacy checkSignatureStatus function for agreement: ${agreementId}`);
  return checkPrimarySignatureStatus(agreementId);
};